import { makeAuthenticatedRequest, makePublicRequest } from '../utils/apiUtils';
import { safeConsole } from '../utils/productionUtils';

/**
 * Tracking Service
 * Handles page view tracking and analytics events
 */

// Generate a unique session ID for the browser session
const generateSessionId = () => {
  let sessionId = sessionStorage.getItem('tracking_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('tracking_session_id', sessionId);
  }
  return sessionId;
};

// Get device information
const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  
  // Detect device type
  let deviceType = 'desktop';
  if (/Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    if (/iPad/i.test(userAgent)) {
      deviceType = 'tablet';
    } else {
      deviceType = 'mobile';
    }
  }

  // Detect browser
  let browser = 'unknown';
  if (userAgent.includes('Chrome')) browser = 'Chrome';
  else if (userAgent.includes('Firefox')) browser = 'Firefox';
  else if (userAgent.includes('Safari')) browser = 'Safari';
  else if (userAgent.includes('Edge')) browser = 'Edge';
  else if (userAgent.includes('Opera')) browser = 'Opera';

  // Detect OS
  let os = 'unknown';
  if (userAgent.includes('Windows')) os = 'Windows';
  else if (userAgent.includes('Mac')) os = 'macOS';
  else if (userAgent.includes('Linux')) os = 'Linux';
  else if (userAgent.includes('Android')) os = 'Android';
  else if (userAgent.includes('iOS')) os = 'iOS';

  return {
    deviceType,
    browser,
    os,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewportSize: `${window.innerWidth}x${window.innerHeight}`
  };
};

class TrackingService {
  constructor() {
    this.sessionId = generateSessionId();
    this.pageStartTime = null;
    this.isTrackingEnabled = true;
  }

  /**
   * Track a page view
   * @param {string} pagePath - The current page path
   * @param {string} pageTitle - The page title
   * @param {string} referrer - The referrer URL
   */
  async trackPageView(pagePath, pageTitle = null, referrer = null) {
    if (!this.isTrackingEnabled) return;

    try {
      // Record page start time for duration tracking
      this.pageStartTime = Date.now();

      const deviceInfo = getDeviceInfo();
      
      const trackingData = {
        page_path: pagePath,
        page_title: pageTitle || document.title,
        referrer: referrer || document.referrer,
        session_id: this.sessionId,
        ...deviceInfo,
        duration_seconds: 0
      };

      safeConsole.log('📊 Tracking page view:', pagePath);

      // Use makePublicRequest (no auth required) for tracking
      const response = await makePublicRequest('/tracking/pageview', {
        method: 'POST',
        body: JSON.stringify(trackingData)
      });

      if (response.success) {
        safeConsole.log('✅ Page view tracked successfully');
      } else {
        safeConsole.warn('⚠️ Failed to track page view:', response.error);
      }

      return response;
    } catch (error) {
      safeConsole.error('❌ Error tracking page view:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Track page exit (duration)
   * @param {string} pagePath - The page path being exited
   */
  async trackPageExit(pagePath) {
    if (!this.isTrackingEnabled || !this.pageStartTime) return;

    try {
      const duration = Math.round((Date.now() - this.pageStartTime) / 1000);
      
      if (duration > 0) {
        await this.trackEvent('page_exit', 'navigation', {
          page_path: pagePath,
          duration_seconds: duration
        });
      }
    } catch (error) {
      safeConsole.error('❌ Error tracking page exit:', error);
    }
  }

  /**
   * Track a custom event
   * @param {string} eventName - Name of the event
   * @param {string} eventCategory - Category of the event
   * @param {object} eventData - Additional event data
   * @param {string} pagePath - Current page path
   */
  async trackEvent(eventName, eventCategory, eventData = {}, pagePath = null) {
    if (!this.isTrackingEnabled) return;

    try {
      const trackingData = {
        event_name: eventName,
        event_category: eventCategory,
        event_data: eventData,
        session_id: this.sessionId,
        page_path: pagePath || window.location.pathname
      };

      safeConsole.log('📊 Tracking event:', eventName, eventCategory);

      // Use makePublicRequest (no auth required) for tracking
      const response = await makePublicRequest('/tracking/event', {
        method: 'POST',
        body: JSON.stringify(trackingData)
      });

      if (response.success) {
        safeConsole.log('✅ Event tracked successfully');
      } else {
        safeConsole.warn('⚠️ Failed to track event:', response.error);
      }

      return response;
    } catch (error) {
      safeConsole.error('❌ Error tracking event:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Track draft room specific events
   */
  async trackDraftRoomEvent(eventName, eventData = {}) {
    return this.trackEvent(eventName, 'draft_room', {
      ...eventData,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track user interaction events
   */
  async trackUserInteraction(action, element, additionalData = {}) {
    return this.trackEvent(action, 'user_interaction', {
      element,
      ...additionalData,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track league management events
   */
  async trackLeagueEvent(eventName, leagueId, eventData = {}) {
    return this.trackEvent(eventName, 'league_management', {
      league_id: leagueId,
      ...eventData,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Get analytics summary
   * @param {string} startDate - Start date for analytics range
   * @param {string} endDate - End date for analytics range
   * @param {string} pagePath - Filter by specific page path
   */
  async getAnalyticsSummary(startDate = null, endDate = null, pagePath = null) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (pagePath) params.append('page_path', pagePath);

      const queryString = params.toString();
      const url = `/tracking/summary${queryString ? `?${queryString}` : ''}`;

      // Analytics summary might need auth depending on your requirements
      const response = await makeAuthenticatedRequest(url);
      
      return response;
    } catch (error) {
      safeConsole.error('❌ Error getting analytics summary:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Enable or disable tracking
   * @param {boolean} enabled - Whether tracking should be enabled
   */
  setTrackingEnabled(enabled) {
    this.isTrackingEnabled = enabled;
    safeConsole.log(`📊 Tracking ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Get current session ID
   */
  getSessionId() {
    return this.sessionId;
  }
}

// Create singleton instance
const trackingService = new TrackingService();

export default trackingService;